import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  Music, LogOut, Plus, Gift, Zap, ChevronRight, ExternalLink, ShoppingCart, ChevronDown, Ticket,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { CreateGiftDialog } from "@/components/CreateGiftDialog";
import TrackRow from "@/components/account/TrackRow";

interface Generation {
  id: string;
  status: string;
  story: string;
  lyrics: string | null;
  demo_url: string | null;
  demo_url_2: string | null;
  is_full_purchased: boolean | null;
  created_at: string;
  full_music_url: string | null;
  full_music_url_2: string | null;
  full_price_rub: number | null;
}

interface GiftPageRow {
  id: string;
  slug: string;
  hero_name: string;
  track_title: string;
  views_count: number;
  play_count: number;
  created_at: string;
  generation_id: string | null;
}

const HIT_PACKAGES = [
  { quantity: 1, price: 249, label: "1 Хит", perHit: 249 },
  { quantity: 5, price: 949, label: "5 Хитов", perHit: 190, badge: "Популярный" },
  { quantity: 10, price: 1649, label: "10 Хитов", perHit: 165, badge: "Выгодный" },
  { quantity: 15, price: 2249, label: "15 Хитов", perHit: 150, badge: "Максимум" },
];

type Tab = "songs" | "balance" | "gifts";

const AccountPage = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();

  const [tab, setTab] = useState<Tab>("songs");
  const [balance, setBalance] = useState(0);
  const [generations, setGenerations] = useState<Generation[]>([]);
  const [giftPages, setGiftPages] = useState<GiftPageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [purchaseLoading, setPurchaseLoading] = useState<number | null>(null);
  const [buyingFullId, setBuyingFullId] = useState<string | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [showGiftUpsell, setShowGiftUpsell] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [promoCode, setPromoCode] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [giftDialogGen, setGiftDialogGen] = useState<Generation | null>(null);

  const getSongTitle = (gen: Generation) => {
    if (gen.lyrics) {
      const firstLine = gen.lyrics.split("\n").find(l => l.trim().length > 0);
      if (firstLine && firstLine.trim().length > 0) return firstLine.trim();
    }
    return gen.story.length > 60 ? gen.story.slice(0, 60) + "…" : gen.story;
  };

  const handleBuyFullTrack = async (generationId: string) => {
    setBuyingFullId(generationId);
    try {
      const response = await supabase.functions.invoke("web-buy-full-track", {
        body: { generation_id: generationId },
      });
      if (response.error) throw new Error(response.error.message);
      const data = response.data;
      if (!data.success) throw new Error(data.error);
      window.location.href = data.payment_url;
    } catch (err: any) {
      toast({ title: "Ошибка", description: err.message, variant: "destructive" });
    } finally {
      setBuyingFullId(null);
    }
  };

  const handleRedeemPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const response = await supabase.functions.invoke("web-redeem-promo", {
        body: { code: promoCode },
      });
      if (response.error) throw new Error(response.error.message);
      const data = response.data;
      if (!data.success) {
        toast({ title: "Ошибка", description: data.error, variant: "destructive" });
        return;
      }
      setBalance(data.new_balance);
      setPromoCode("");
      if (data.auto_unlocked) {
        toast({ title: "🎉 Промокод активирован!", description: `+${data.hits_added} хит(ов). Полная версия песни открыта автоматически!` });
        // Reload generations
        const { data: webUser } = await supabase.from("web_users").select("id").eq("auth_user_id", user!.id).single();
        if (webUser) {
          const { data: gens } = await supabase
            .from("generations")
            .select("id, status, story, lyrics, demo_url, demo_url_2, is_full_purchased, created_at, full_music_url, full_music_url_2, full_price_rub, web_user_id")
            .eq("web_user_id", webUser.id)
            .order("created_at", { ascending: false })
            .limit(50);
          if (gens) setGenerations(gens as Generation[]);
        }
      } else {
        toast({ title: "🎉 Промокод активирован!", description: `+${data.hits_added} хит(ов) добавлено на баланс!` });
      }
    } catch (err: any) {
      toast({ title: "Ошибка", description: err.message, variant: "destructive" });
    } finally {
      setPromoLoading(false);
    }
  };

  // Show payment success toast
  useEffect(() => {
    const paymentParam = searchParams.get("payment");
    if (paymentParam === "success") {
      toast({ title: "Оплата прошла!", description: "Хиты зачислены на ваш баланс." });
      navigate("/account", { replace: true });
    } else if (paymentParam === "full_track_success") {
      toast({ title: "🎉 Полная версия получена!", description: "Ваш хит доступен для скачивания." });
      setShowGiftUpsell(true);
      navigate("/account", { replace: true });
    } else if (paymentParam === "gift_success") {
      toast({ title: "🎁 Подарочная страница создана!", description: "Посмотрите её во вкладке «Подарки»." });
      setTab("gifts");
      navigate("/account", { replace: true });
    }
  }, [searchParams, toast, navigate]);

  // Load data
  useEffect(() => {
    if (!user) return;
    const load = async () => {
      setLoading(true);
      const [profileRes, gensRes, giftsRes] = await Promise.all([
        supabase.from("web_users").select("wow_hits_balance").eq("auth_user_id", user.id).single(),
        supabase
          .from("generations")
          .select("id, status, story, lyrics, demo_url, demo_url_2, is_full_purchased, created_at, full_music_url, full_music_url_2, full_price_rub, web_user_id")
          .not("web_user_id", "is", null)
          .order("created_at", { ascending: false })
          .limit(50),
        supabase.from("gift_pages").select("id, slug, hero_name, track_title, views_count, play_count, created_at, generation_id").order("created_at", { ascending: false }).limit(50),
      ]);

      if (profileRes.data) setBalance(profileRes.data.wow_hits_balance);

      // Filter generations belonging to this user (web_user_id matches)
      if (gensRes.data) {
        // We need to filter by the user's web_user_id
        const { data: webUser } = await supabase
          .from("web_users")
          .select("id")
          .eq("auth_user_id", user.id)
          .single();

        if (webUser) {
          const userGens = gensRes.data.filter((g: any) => g.web_user_id === webUser.id);
          setGenerations(userGens as Generation[]);
        }
      }

      // Filter gifts belonging to this user's generations
      if (giftsRes.data && gensRes.data) {
        const { data: webUser2 } = await supabase
          .from("web_users")
          .select("id")
          .eq("auth_user_id", user.id)
          .single();
        if (webUser2) {
          const userGenIds = new Set(
            (gensRes.data as any[])
              .filter((g: any) => g.web_user_id === webUser2.id)
              .map((g: any) => g.id)
          );
          setGiftPages(
            (giftsRes.data as any[]).filter((g: any) => g.generation_id && userGenIds.has(g.generation_id))
          );
        }
      }
      setLoading(false);
    };
    load();
  }, [user]);

  const handleBuyHits = async (quantity: number) => {
    setPurchaseLoading(quantity);
    try {
      const response = await supabase.functions.invoke("web-purchase", {
        body: { quantity },
      });

      if (response.error) throw new Error(response.error.message);
      const data = response.data;
      if (!data.success) throw new Error(data.error);

      // Redirect to YooKassa
      window.location.href = data.payment_url;
    } catch (err: any) {
      toast({ title: "Ошибка оплаты", description: err.message, variant: "destructive" });
    } finally {
      setPurchaseLoading(null);
    }
  };

  const togglePlay = useCallback((url: string, id: string) => {
    const audio = audioElementRef.current;
    if (!audio) return;

    if (playingId === id) {
      // Same track — toggle
      if (audio.paused) {
        audio.play().catch(() => setPlayingId(null));
      } else {
        audio.pause();
      }
      return;
    }

    // Switch track
    setAudioProgress(0);
    setAudioDuration(0);
    audio.src = url;
    audio.load();
    audio.play().catch(() => setPlayingId(null));
    setPlayingId(id);
  }, [playingId]);

  const handleSeek = useCallback((pct: number) => {
    const audio = audioElementRef.current;
    if (!audio || !audioDuration) return;
    audio.currentTime = pct * audioDuration;
    setAudioProgress(pct * audioDuration);
  }, [audioDuration]);

  // Audio event listeners
  useEffect(() => {
    const audio = new Audio();
    audioElementRef.current = audio;

    const onTimeUpdate = () => setAudioProgress(audio.currentTime);
    const onLoadedMetadata = () => setAudioDuration(audio.duration);
    const onPlay = () => setIsAudioPlaying(true);
    const onPause = () => setIsAudioPlaying(false);
    const onEnded = () => {
      setPlayingId(null);
      setAudioProgress(0);
      setIsAudioPlaying(false);
    };

    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);

    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
      audio.pause();
      audio.src = "";
    };
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const statusLabels: Record<string, string> = {
    pending: "Ожидание",
    generating_text: "Пишем текст...",
    text_ready: "Текст готов",
    generating_music: "Создаём музыку...",
    completed: "Готово ✓",
    failed: "Ошибка",
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <main className="flex-1 pt-24 pb-16 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-display font-bold text-foreground">Личный кабинет</h1>
              <p className="text-sm text-muted-foreground mt-1">{user?.email}</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 bg-primary/10 text-primary px-3 py-1.5 rounded-full text-sm font-medium">
                <Zap className="w-4 h-4" />
                {balance} хит{balance === 1 ? "" : balance < 5 ? "а" : "ов"}
              </div>
              <Button variant="ghost" size="sm" onClick={handleSignOut} className="text-muted-foreground">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Gift Upsell Banner */}
          {showGiftUpsell && (
            <div className="mb-6 p-5 rounded-xl bg-primary/10 border border-primary/20 relative">
              <button
                onClick={() => setShowGiftUpsell(false)}
                className="absolute top-2 right-2 text-muted-foreground hover:text-foreground text-lg leading-none"
              >
                ×
              </button>
              <div className="flex items-start gap-4">
                <div className="text-3xl">🎁</div>
                <div className="flex-1">
                  <h3 className="font-display font-bold text-foreground">Сделай подарок, который запомнят!</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Превратите хит в красивую подарочную страницу с анимацией, личным посланием и премиальным дизайном — бесплатно
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    ✨ Эффект открытия • 🎨 Премиальный дизайн • 💌 Личное сообщение • 🔗 Ссылка для отправки
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-1 bg-secondary rounded-xl p-1 mb-6">
            {([
              { key: "songs" as Tab, label: "Мои песни", icon: Music },
              { key: "balance" as Tab, label: "Баланс", icon: Zap },
              { key: "gifts" as Tab, label: "Подарки", icon: Gift },
            ]).map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  tab === key ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : (
            <>
              {/* Tab: Songs */}
              {tab === "songs" && (
                <div className="space-y-3">
                  {generations.length === 0 ? (
                    <div className="text-center py-16">
                      <Music className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                      <p className="text-muted-foreground mb-4">У вас пока нет песен</p>
                      <Button onClick={() => navigate("/create")} className="gap-2">
                        <Plus className="w-4 h-4" /> Создать первый хит
                      </Button>
                    </div>
                  ) : (
                    generations.map((gen) => {
                      const isExpanded = expandedId === gen.id;
                      const isPurchased = gen.is_full_purchased === true;
                      return (
                        <div key={gen.id} className="bg-card rounded-xl border border-border overflow-hidden transition-all">
                          {/* Collapsed header */}
                          <button
                            onClick={() => setExpandedId(isExpanded ? null : gen.id)}
                            className="w-full p-4 flex items-center justify-between gap-3 text-left hover:bg-muted/30 transition-colors"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground line-clamp-1">{getSongTitle(gen)}</p>
                              <div className="flex items-center gap-2 mt-1.5">
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  gen.status === "completed" ? "bg-green-500/10 text-green-600" :
                                  gen.status === "failed" ? "bg-destructive/10 text-destructive" :
                                  "bg-primary/10 text-primary"
                                }`}>
                                  {isPurchased ? "Полная версия ✓" : (statusLabels[gen.status] || gen.status)}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {new Date(gen.created_at).toLocaleDateString("ru-RU")}
                                </span>
                              </div>
                            </div>
                            <ChevronDown className={`w-5 h-5 text-muted-foreground shrink-0 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`} />
                          </button>

                          {/* Expanded content */}
                          {isExpanded && gen.status === "completed" && (
                            <div className="px-4 pb-4 pt-1 border-t border-border space-y-3">
                              {isPurchased ? (
                                <>
                                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Полные версии</p>
                                  {gen.full_music_url && (
                                    <TrackRow
                                      label="Полная версия 1"
                                      url={gen.full_music_url}
                                      trackId={`${gen.id}-full1`}
                                      activeTrackId={playingId}
                                      isPlaying={isAudioPlaying}
                                      progress={audioProgress}
                                      duration={audioDuration}
                                      onTogglePlay={togglePlay}
                                      onSeek={handleSeek}
                                      showDownload
                                    />
                                  )}
                                  {gen.full_music_url_2 && (
                                    <TrackRow
                                      label="Полная версия 2"
                                      url={gen.full_music_url_2}
                                      trackId={`${gen.id}-full2`}
                                      activeTrackId={playingId}
                                      isPlaying={isAudioPlaying}
                                      progress={audioProgress}
                                      duration={audioDuration}
                                      onTogglePlay={togglePlay}
                                      onSeek={handleSeek}
                                      showDownload
                                    />
                                  )}
                                   {(gen.full_music_url || gen.full_music_url_2) && (
                                     <Button variant="outline" size="sm" className="w-full gap-2 mt-1" onClick={() => setGiftDialogGen(gen)}>
                                       <Gift className="w-4 h-4" /> Создать подарочную страницу — бесплатно
                                     </Button>
                                   )}
                                </>
                              ) : (
                                <>
                                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Демо версии</p>
                                  {gen.demo_url && (
                                    <TrackRow
                                      label="Демо 1"
                                      url={gen.demo_url}
                                      trackId={`${gen.id}-demo1`}
                                      activeTrackId={playingId}
                                      isPlaying={isAudioPlaying}
                                      progress={audioProgress}
                                      duration={audioDuration}
                                      onTogglePlay={togglePlay}
                                      onSeek={handleSeek}
                                    />
                                  )}
                                  {gen.demo_url_2 && (
                                    <TrackRow
                                      label="Демо 2"
                                      url={gen.demo_url_2}
                                      trackId={`${gen.id}-demo2`}
                                      activeTrackId={playingId}
                                      isPlaying={isAudioPlaying}
                                      progress={audioProgress}
                                      duration={audioDuration}
                                      onTogglePlay={togglePlay}
                                      onSeek={handleSeek}
                                    />
                                  )}
                                  <Button
                                    onClick={() => handleBuyFullTrack(gen.id)}
                                    disabled={buyingFullId !== null}
                                    className="w-full gap-2 mt-1"
                                  >
                                    {buyingFullId === gen.id ? (
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground" />
                                    ) : (
                                      <ShoppingCart className="w-4 h-4" />
                                    )}
                                    Купить полную версию — {gen.full_price_rub || 249}₽
                                  </Button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}

                  {generations.length > 0 && (
                    <Button onClick={() => navigate("/create")} variant="outline" className="w-full gap-2">
                      <Plus className="w-4 h-4" /> Создать ещё
                    </Button>
                  )}
                </div>
              )}

              {/* Tab: Balance */}
              {tab === "balance" && (
                <div className="space-y-6">
                  <div className="bg-card rounded-xl border border-border p-6 text-center">
                    <p className="text-sm text-muted-foreground">Текущий баланс</p>
                    <p className="text-4xl font-display font-bold text-primary mt-2">{balance}</p>
                    <p className="text-sm text-muted-foreground mt-1">хит{balance === 1 ? "" : balance < 5 ? "а" : "ов"}</p>
                  </div>

                  <div>
                    <h2 className="text-lg font-display font-bold text-foreground mb-3">Пополнить баланс</h2>
                    <div className="grid gap-3">
                      {HIT_PACKAGES.map((pkg) => (
                        <button
                          key={pkg.quantity}
                          onClick={() => handleBuyHits(pkg.quantity)}
                          disabled={purchaseLoading !== null}
                          className="relative bg-card rounded-xl border-2 border-border hover:border-primary p-4 flex items-center justify-between transition-all group"
                        >
                          {pkg.badge && (
                            <span className="absolute -top-2.5 left-4 text-xs font-bold bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                              {pkg.badge}
                            </span>
                          )}
                          <div className="text-left">
                            <p className="font-bold text-foreground">{pkg.label}</p>
                            <p className="text-xs text-muted-foreground">{pkg.perHit}₽ за хит</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-lg font-bold text-foreground">{pkg.price}₽</span>
                            {purchaseLoading === pkg.quantity ? (
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Promo code */}
                  <div className="bg-card rounded-xl border border-border p-5">
                    <h2 className="text-lg font-display font-bold text-foreground mb-3 flex items-center gap-2">
                      <Ticket className="w-5 h-5 text-primary" /> Промокод
                    </h2>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Введите промокод"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                        onKeyDown={(e) => e.key === "Enter" && handleRedeemPromo()}
                        className="flex-1"
                        maxLength={50}
                      />
                      <Button onClick={handleRedeemPromo} disabled={promoLoading || !promoCode.trim()}>
                        {promoLoading ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground" />
                        ) : (
                          "Применить"
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab: Gifts */}
              {tab === "gifts" && (
                <div className="space-y-3">
                  {giftPages.length === 0 ? (
                    <div className="text-center py-16">
                      <Gift className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                      <p className="text-muted-foreground">У вас пока нет подарочных страниц</p>
                    </div>
                  ) : (
                    giftPages.map((g) => (
                      <a
                        key={g.id}
                        href={`/g/${g.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block bg-card rounded-xl border border-border p-4 hover:border-primary/50 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-foreground">{g.track_title}</p>
                            <p className="text-sm text-muted-foreground mt-0.5">
                              для {g.hero_name} • {g.views_count} просм. • {g.play_count} прослуш.
                            </p>
                          </div>
                          <ExternalLink className="w-4 h-4 text-muted-foreground shrink-0" />
                        </div>
                      </a>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
      {giftDialogGen && (
        <CreateGiftDialog
          open={!!giftDialogGen}
          onOpenChange={(open) => !open && setGiftDialogGen(null)}
          generationId={giftDialogGen.id}
          trackTitle={getSongTitle(giftDialogGen)}
          audioUrl={giftDialogGen.full_music_url!}
          audioUrl2={giftDialogGen.full_music_url_2}
        />
      )}
    </div>
  );
};

export default AccountPage;
